import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response && err.response.status !== 401) {
        // Non-401 auth error swallowed silently in production.
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip if returning from OAuth callback — AuthCallback establishes session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // JWT is set as httpOnly cookie by the backend. We only keep user in state.
    setUser(data.user);
    return data.user;
  }, []);

  const signupWithPassword = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout errors are non-fatal — we clear local state regardless.
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, loginWithPassword, signupWithPassword, logout, checkAuth }),
    [user, loading, loginWithPassword, signupWithPassword, logout, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
